/**
 * @prettier
 */
import {log as logToServer} from '../../api/logger';

const GENERAL = 'GENERAL';
const DINING = 'DINING';
const KITCHEN = 'KITCHEN';
const OUTSIDE = 'OUTSIDE';
const POOL_SPA = 'POOL_SPA';
const SUITABILITY = 'SUITABILITY';
const LOCATION_TYPE = 'LOCATION_TYPE';
const ENTERTAINMENT = 'ENTERTAINMENT';
const NOTES = 'NOTES';
const FEATURED_AMENITIES = 'FEATURED_AMENITIES';
const POOL = 'POOL';
const INTERNET = 'INTERNET';
const AIR_CONDITIONING = 'AIR_CONDITIONING';
const HOT_TUB = 'HOT_TUB';
const PETS = 'PETS';
const FIREPLACE = 'FIREPLACE';
const TV = 'TV';
const CABLE = 'CABLE';
const WASHER_DRYER = 'WASHER_DRYER';
const CHILDREN_WELCOME = 'CHILDREN_WELCOME';
const PARKING = 'PARKING';
const NO_SMOKING = 'NO_SMOKING';
const WHEELCHAIR = 'WHEELCHAIR';
const HEATER = 'HEATER';

const pdsGroupToKeyMap = {
    GENERAL: 'property.amenities.general',
    DINING: 'property.amenities.dining',
    KITCHEN: 'property.amenities.kitchen',
    OUTSIDE: 'property.amenities.outside',
    POOL_SPA: 'property.amenities.poolSpa',
    SUITABILITY: 'property.amenities.suitability',
    LOCATION_TYPE: 'property.amenities.locationType',
    ENTERTAINMENT: 'property.amenities.entertainment',
    NOTES: 'property.amenities.notes'
};

const gqlGroupToKeyMap = {
    GENERAL: [GENERAL],
    DINING: [DINING, 'DINING_CHECKBOX'],
    KITCHEN: [KITCHEN],
    OUTSIDE: [OUTSIDE],
    POOL_SPA: [POOL_SPA],
    SUITABILITY: [SUITABILITY, 'SUITABILITY_CHKBOX'],
    LOCATION_TYPE: [LOCATION_TYPE],
    ENTERTAINMENT: [ENTERTAINMENT]
};

const gqlFeaturedAmenityToAmenityNameMap = {
    POOL: ['COMMUNAL_POOL', 'PRIVATE_POOL'],
    INTERNET: ['INTERNET_ACCESS'],
    AIR_CONDITIONING: ['AIR_CONDITIONING'],
    HOT_TUB: ['HOT_TUB', 'SPA_OR_WHIRLPOOL'],
    PETS: ['PETS_CONSIDERED'],
    FIREPLACE: ['FIREPLACE'],
    TV: ['TV'],
    CABLE: ['SATELLITE_OR_CABLE'],
    WASHER_DRYER: ['WASHING_MACHINE', 'CLOTHES_DRYER'],
    CHILDREN_WELCOME: ['CHILDREN_WELCOME'],
    PARKING: ['PARKING'],
    NO_SMOKING: ['NON_SMOKING_ONLY'],
    WHEELCHAIR: ['WHEELCHAIR_ACCESSIBLE'],
    HEATER: ['HEATER', 'CENTRAL_HEATING']
};

/* eslint-disable complexity */
function getDiff(arr1, arr2) {
    const diff = [];

    if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
        return diff;
    }

    for (let val of arr1) {
        if (arr2.indexOf(val) === -1) {
            diff.push(`- ${val}`);
        }
    }

    for (let val of arr2) {
        if (arr1.indexOf(val) === -1) {
            diff.push(`+ ${val}`);
        }
    }

    return diff;
}
/* eslint-enable complexity */

/* eslint-disable complexity */
function logAmenitesByGroup(listingId, pdsAmenities, graphqlAmenities) {
    const {propertyContentItemsData, unitContentItemsData} = graphqlAmenities;

    const groupsToLog = [
        GENERAL,
        DINING,
        KITCHEN,
        OUTSIDE,
        POOL_SPA,
        SUITABILITY,
        LOCATION_TYPE,
        ENTERTAINMENT
    ];
    const dataSummary = {};

    for (let group of groupsToLog) {
        // For PDS, find the amenity group whose key matches the key map above
        // For GQL, find all amenities with tag that matches one of the group names from the map above
        const pdsAttributes =
            (pdsAmenities.find((obj) => obj.key === pdsGroupToKeyMap[group]) || {})
                .attributeObjects || [];
        const gqlAttributes = [...propertyContentItemsData, ...unitContentItemsData].filter((obj) =>
            (obj.amenity.tags || []).some((key) => gqlGroupToKeyMap[group].indexOf(key) > -1)
        );
        const pdsVals = [];
        const gqlVals = [];
        let pdsDescs = [];
        let gqlDescs = [];

        for (let pdsAttribute of pdsAttributes) {
            pdsVals.push(pdsAttribute.label);
            pdsDescs.push(pdsAttribute.description);
        }

        for (let gqlAttribute of gqlAttributes) {
            gqlVals.push(gqlAttribute.amenity.displayName);
            gqlDescs.push(
                (
                    (gqlAttribute.stringAttributeValues || []).find(
                        (obj) => obj.attributeName === 'description'
                    ) || {}
                ).attributeValue
            );
        }

        // remove any falsey values
        pdsDescs = pdsDescs.filter(Boolean);
        gqlDescs = gqlDescs.filter(Boolean);

        // find any values that differ
        const valDiff = getDiff(pdsVals, gqlVals);
        const descDiff = getDiff(pdsDescs, gqlDescs);

        dataSummary[group] = {
            numValsFromPds: pdsAttributes.length,
            numValsFromGql: gqlAttributes.length,
            valDiff,
            numsMatch: pdsAttributes.length === gqlAttributes.length,
            valsMatch: valDiff.length === 0,
            descMatch: descDiff.length === 0
        };
    }

    logToServer({
        description: 'PDS vs GQL Amenity Values by Group',
        listingId,
        ...dataSummary
    });
}
/* eslint-enable complexity */

/* eslint-disable complexity */
function logAmenitiesNotesData(listingId, pdsAmenities, graphqlAmenities) {
    const {unitMetadataData} = graphqlAmenities;

    const pdsAttributes =
        (pdsAmenities.find((obj) => obj.key === pdsGroupToKeyMap[NOTES]) || {}).attributeObjects ||
        [];
    const pdsNote = ((pdsAttributes || [])[0] || {}).label;
    const gqlNote = (unitMetadataData || {}).featuresDescription;
    const dataSummary = {};

    dataSummary[NOTES] = {
        pdsNote,
        gqlNote,
        notesMatch: (pdsNote || !!pdsNote) === (gqlNote || !!gqlNote)
    };

    logToServer({
        description: 'PDS vs GQL Amenity Notes',
        listingId,
        ...dataSummary
    });
}
/* eslint-enable complexity */

function logFeaturedAmenitiesData(listingId, pdsAmenities, graphqlAmenities) {
    const featuredAmenities = [
        POOL,
        INTERNET,
        AIR_CONDITIONING,
        HOT_TUB,
        PETS,
        FIREPLACE,
        TV,
        CABLE,
        WASHER_DRYER,
        CHILDREN_WELCOME,
        PARKING,
        NO_SMOKING,
        WHEELCHAIR,
        HEATER
    ];
    const {unitContentItemsData} = graphqlAmenities;
    const pdsFeaturedAmenities = pdsAmenities;
    let gqlFeaturedAmenities = [];

    for (let amenity of featuredAmenities) {
        const amenityHasMatchFromGql = unitContentItemsData.find(
            (obj) => gqlFeaturedAmenityToAmenityNameMap[amenity].indexOf(obj.amenityName) > -1
        );

        if (amenityHasMatchFromGql) {
            gqlFeaturedAmenities.push(amenity);
        }
    }

    const diff = getDiff(pdsFeaturedAmenities, gqlFeaturedAmenities);
    const dataSummary = {};

    dataSummary[FEATURED_AMENITIES] = {
        pdsFeaturedAmenities,
        gqlFeaturedAmenities,
        diff,
        isMatch: diff.length === 0
    };

    logToServer({
        description: 'PDS vs GQL Featured Amenities',
        listingId,
        ...dataSummary
    });
}

function logAmenitiesData(listingId, pdsAmenities, graphqlAmenities) {
    logAmenitesByGroup(listingId, pdsAmenities.amenities, graphqlAmenities);
    logAmenitiesNotesData(listingId, pdsAmenities.amenities, graphqlAmenities);
    logFeaturedAmenitiesData(listingId, pdsAmenities.featuredAmenities, graphqlAmenities);
}

export default logAmenitiesData;
export {
    getDiff,
    logAmenitesByGroup,
    logAmenitiesNotesData,
    logFeaturedAmenitiesData,
    logAmenitiesData
};
